import { useCallback, useEffect, useRef, useState } from 'react'

interface Recording {
  recorder: MediaRecorder
  stream: MediaStream
  chunks: Blob[]
}

// MP4 (AAC) を優先し、未対応のブラウザだけ既定 (WebM / Opus) に任せる (録音エンジンと同じ)
const MIME_TYPE = 'audio/mp4'

/**
 * 欄の mic。押す → 話す → 押す で録った音声を返す (design-book 04-apps/kakeru.md がんの相談)。
 * 書き起こしと送信は呼ぶ側。音声はどこにも残さない。
 */
export const useVoiceInput = () => {
  const recordingRef = useRef<Recording | null>(null)
  const [isRecording, setIsRecording] = useState(false)

  const start = useCallback(async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      throw new Error('no recorder')
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const recorder = new MediaRecorder(
      stream,
      MediaRecorder.isTypeSupported(MIME_TYPE)
        ? { mimeType: MIME_TYPE }
        : undefined
    )
    const recording: Recording = {
      recorder,
      stream,
      chunks: [],
    }
    recorder.ondataavailable = (e) => {
      if (e.data.size) recording.chunks.push(e.data)
    }
    recorder.start()
    recordingRef.current = recording
    setIsRecording(true)
  }, [])

  const stop = useCallback(
    () =>
      new Promise<Blob>((resolve, reject) => {
        const recording = recordingRef.current
        recordingRef.current = null
        setIsRecording(false)
        if (!recording) {
          reject(new Error('not recording'))
          return
        }
        const { recorder, stream, chunks } = recording
        recorder.onstop = () => {
          stream.getTracks().forEach((track) => track.stop())
          resolve(new Blob(chunks, { type: recorder.mimeType || MIME_TYPE }))
        }
        recorder.stop()
      }),
    []
  )

  // 録っている途中でページを離れたら、マイクを放す
  useEffect(
    () => () => {
      recordingRef.current?.stream.getTracks().forEach((track) => track.stop())
    },
    []
  )

  return {
    isRecording,
    start,
    stop,
  }
}
